import type { AccountInput } from "@moneymap/contracts";
import {
  Button,
  Callout,
  Card,
  FormField,
  Modal,
  PageHeader,
  SectionHeader,
  SelectInput,
  StatePanel,
  StatusBadge,
  TextInput,
} from "@moneymap/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useApi } from "../api/ApiProvider";
import { MoneyMapApiClient } from "../api/client";
import type { AccountRead, SettingsRead } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { QueryState } from "../components/QueryState";
import { formatInstant, formatMoney, parseMoneyInput, titleFromMachineValue } from "../lib/format";
import {
  openPlaidLink,
  PLAID_OAUTH_RETURN_PATH_KEY,
  PLAID_OAUTH_TOKEN_KEY,
} from "../lib/plaid-link";

export function NeedsConfirmationPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [activeItemId, setActiveItemId] = useState<string>();
  const [note, setNote] = useState("");
  const query = useQuery({
    queryKey: ["needs-confirmation"],
    queryFn: () => api.getNeedsConfirmation(),
  });
  const mutation = useMutation({
    mutationFn: (input: { id: string; decision: string; note?: string }) =>
      api.resolveConfirmation(input.id, {
        decision: input.decision,
        ...(input.note ? { note: input.note } : {}),
      }),
    onSuccess: async () => {
      setActiveItemId(undefined);
      setNote("");
      await queryClient.invalidateQueries({ queryKey: ["needs-confirmation"] });
      await queryClient.invalidateQueries({ queryKey: ["briefing"] });
    },
  });
  const items = query.data?.data.items ?? [];
  const activeItem = items.find((item) => item.id === activeItemId);
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Explicit uncertainty"
        title="Needs confirmation"
        description="Low-confidence recurring bills, transfers, income, changed amounts, and Spark deposits stay out of decisive calculations when exclusion is safer."
      />
      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
      >
        {items.length === 0 ? (
          <StatePanel title="Nothing needs confirmation">
            IterNest Money will put material unknowns here rather than silently inventing a
            transaction, bill date, or income amount.
          </StatePanel>
        ) : (
          <div className="card-list">
            {items.map((item) => (
              <Card key={item.id}>
                <div className="section-stack">
                  <div className="list-row">
                    <div className="list-row__main">
                      <strong>{item.title}</strong>
                      <span>
                        {titleFromMachineValue(item.type)} ·{" "}
                        {titleFromMachineValue(item.confidence)} confidence
                      </span>
                    </div>
                    <StatusBadge status="needs_confirmation" />
                  </div>
                  <p>{item.description}</p>
                  <Callout title="Why this matters" tone="warning">
                    {item.impact}
                  </Callout>
                  <div className="button-row">
                    {item.options.includes("approve") || item.options.includes("confirm") ? (
                      <Button
                        type="button"
                        isLoading={mutation.isPending && activeItemId === item.id}
                        onClick={() =>
                          mutation.mutate({
                            id: item.id,
                            decision: item.options.includes("approve") ? "approve" : "confirm",
                          })
                        }
                      >
                        Confirm
                      </Button>
                    ) : null}
                    {item.options.includes("edit") ? (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setActiveItemId(item.id)}
                      >
                        Review details
                      </Button>
                    ) : null}
                    {item.options.includes("dismiss") || item.options.includes("reject") ? (
                      <Button
                        type="button"
                        variant="quiet"
                        onClick={() =>
                          mutation.mutate({
                            id: item.id,
                            decision: item.options.includes("dismiss") ? "dismiss" : "reject",
                          })
                        }
                      >
                        Not this
                      </Button>
                    ) : null}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </QueryState>
      {mutation.error ? (
        <Callout title="Confirmation not saved" tone="urgent">
          {mutation.error.message}
        </Callout>
      ) : null}
      <Modal
        isOpen={Boolean(activeItem)}
        title={activeItem?.title ?? "Review details"}
        description="Add a correction note for the server-side review flow. The original source remains in audit history."
        onClose={() => setActiveItemId(undefined)}
        footer={
          <>
            <Button type="button" variant="quiet" onClick={() => setActiveItemId(undefined)}>
              Cancel
            </Button>
            <Button
              type="button"
              isLoading={mutation.isPending}
              onClick={() => {
                if (activeItem)
                  mutation.mutate({
                    id: activeItem.id,
                    decision: "edit",
                    ...(note ? { note } : {}),
                  });
              }}
            >
              Save review
            </Button>
          </>
        }
      >
        <FormField label="What should IterNest Money know?" htmlFor="confirmation-note">
          <textarea
            id="confirmation-note"
            className="mm-input"
            value={note}
            onChange={(event) => setNote(event.currentTarget.value)}
          />
        </FormField>
      </Modal>
    </div>
  );
}

const settingsLinks = [
  { to: "/settings", label: "Overview", end: true },
  { to: "/settings/connections", label: "Accounts & imports" },
  { to: "/settings/rules", label: "Rules" },
  { to: "/settings/notifications", label: "Notifications" },
  { to: "/settings/privacy", label: "Privacy & data" },
  { to: "/settings/feature-flags", label: "Feature flags" },
] as const;

export function SettingsNavigation() {
  return (
    <nav className="segmented settings-nav" aria-label="Settings sections">
      {settingsLinks.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={"end" in item ? item.end : false}
          className={({ isActive }) => (isActive ? "is-active" : "")}
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function SettingsPage() {
  const api = useApi();
  const query = useQuery({ queryKey: ["settings"], queryFn: () => api.getSettings() });
  const settings = query.data?.data;
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Household control"
        title="Settings"
        description="Edit rules, privacy choices, and account data without changing source code."
      />
      <SettingsNavigation />
      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
      >
        {settings ? (
          <>
            <Card>
              <SectionHeader
                title={settings.household.name}
                description={settings.household.baseCurrency + " · " + settings.profile.timezone}
              />
              <div className="summary-grid">
                <div className="summary-tile">
                  <span>Planning horizon</span>
                  <strong>{settings.household.financialHorizonDays} days</strong>
                </div>
                <div className="summary-tile">
                  <span>Debt strategy</span>
                  <strong>{titleFromMachineValue(settings.household.debtStrategy)}</strong>
                </div>
                <div className="summary-tile">
                  <span>Support code</span>
                  <strong>{settings.supportCode}</strong>
                </div>
              </div>
            </Card>
            <div className="settings-card-grid">
              {settingsLinks.slice(1).map((link) => (
                <Link className="settings-link-card" to={link.to} key={link.to}>
                  <strong>{link.label}</strong>
                  <span>Open {link.label.toLowerCase()}</span>
                </Link>
              ))}
            </div>
            <p className="small muted">
              IterNest Money provides household organization and decision support. It does not
              provide legal, tax, investment, or individualized professional financial advice.
            </p>
          </>
        ) : null}
      </QueryState>
    </div>
  );
}

type AccountEditorValues = {
  name: string;
  type: AccountInput["type"];
  currentBalance: string;
  availableBalance: string;
  purposeId: string;
  protectionLevel: AccountInput["protectionLevel"];
};

function AccountEditor({
  account,
  isOpen,
  onClose,
}: {
  account: AccountRead | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string>();
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const purposesQuery = useQuery({
    queryKey: ["purposes"],
    queryFn: () => api.getPurposes(),
    enabled: isOpen,
  });
  const { register, handleSubmit } = useForm<AccountEditorValues>({
    values: {
      name: account?.name ?? "",
      type: account?.type ?? "checking",
      currentBalance: account ? editableMinor(account.currentBalanceMinor) : "",
      availableBalance: account?.availableBalanceMinor
        ? editableMinor(account.availableBalanceMinor)
        : "",
      purposeId: account?.purposeId ?? "",
      protectionLevel: account?.protectionLevel ?? "standard",
    },
  });
  const saveMutation = useMutation({
    mutationFn: (input: AccountInput) =>
      account ? api.updateAccount(account.id, input, account.version) : api.createAccount(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
      await queryClient.invalidateQueries({ queryKey: ["briefing"] });
      onClose();
    },
  });
  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!account) throw new Error("No account is selected.");
      return api.deleteAccount(account.id, account.version);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
      await queryClient.invalidateQueries({ queryKey: ["briefing"] });
      onClose();
    },
  });
  const submit = handleSubmit((values) => {
    setFormError(undefined);
    const currentBalanceMinor = parseMoneyInput(values.currentBalance);
    const availableBalanceMinor = values.availableBalance
      ? parseMoneyInput(values.availableBalance)
      : null;
    if (!currentBalanceMinor || (values.availableBalance && !availableBalanceMinor)) {
      setFormError("Enter balances as dollars and cents.");
      return;
    }
    saveMutation.mutate({
      name: values.name,
      type: values.type,
      currentBalanceMinor,
      ...(availableBalanceMinor ? { availableBalanceMinor } : {}),
      currency: account?.currency ?? "USD",
      ...(values.purposeId ? { purposeId: values.purposeId } : {}),
      protectionLevel: values.protectionLevel,
    });
  });
  return (
    <Modal
      isOpen={isOpen}
      title={account ? "Edit account" : "Add a manual account"}
      description="Balance changes are explicit user corrections and trigger a new calculation."
      onClose={onClose}
      footer={
        isDeleting ? (
          <>
            <Button type="button" variant="quiet" onClick={() => setIsDeleting(false)}>
              Back
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={deleteConfirmation !== "DELETE"}
              isLoading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              Delete account
            </Button>
          </>
        ) : (
          <>
            {account ? (
              <Button type="button" variant="danger" onClick={() => setIsDeleting(true)}>
                Delete
              </Button>
            ) : null}
            <Button type="button" variant="quiet" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" form="account-editor-form" isLoading={saveMutation.isPending}>
              Save account
            </Button>
          </>
        )
      }
    >
      {isDeleting ? (
        <div className="form-stack">
          <Callout title="Coverage will be recalculated" tone="urgent">
            The account leaves active calculations. Audit history remains according to retention
            policy.
          </Callout>
          <FormField label="Type DELETE to confirm" htmlFor="delete-account-confirmation">
            <TextInput
              id="delete-account-confirmation"
              value={deleteConfirmation}
              onChange={(event) => setDeleteConfirmation(event.currentTarget.value)}
            />
          </FormField>
          {deleteMutation.error ? (
            <Callout title="Account not deleted" tone="urgent">
              {deleteMutation.error.message}
            </Callout>
          ) : null}
        </div>
      ) : (
        <form id="account-editor-form" className="form-stack" onSubmit={submit}>
          <div className="form-grid form-grid--two">
            <FormField label="Name" htmlFor="settings-account-name">
              <TextInput id="settings-account-name" {...register("name", { required: true })} />
            </FormField>
            <FormField label="Type" htmlFor="settings-account-type">
              <SelectInput id="settings-account-type" {...register("type")}>
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
                <option value="credit">Credit card</option>
                <option value="loan">Loan</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </SelectInput>
            </FormField>
            <FormField label="Current balance" htmlFor="settings-account-current">
              <TextInput
                id="settings-account-current"
                inputMode="decimal"
                {...register("currentBalance", { required: true })}
              />
            </FormField>
            <FormField label="Available balance" htmlFor="settings-account-available">
              <TextInput
                id="settings-account-available"
                inputMode="decimal"
                {...register("availableBalance")}
              />
            </FormField>
            <FormField label="Purpose" htmlFor="settings-account-purpose">
              <SelectInput id="settings-account-purpose" {...register("purposeId")}>
                <option value="">Not assigned</option>
                {(purposesQuery.data?.data ?? []).map((purpose) => (
                  <option key={purpose.id} value={purpose.id}>
                    {purpose.name}
                  </option>
                ))}
              </SelectInput>
            </FormField>
            <FormField label="Protection" htmlFor="settings-account-protection">
              <SelectInput id="settings-account-protection" {...register("protectionLevel")}>
                <option value="none">Not protected</option>
                <option value="standard">Standard</option>
                <option value="protected">Protected</option>
                <option value="emergency_only">Emergency only</option>
              </SelectInput>
            </FormField>
          </div>
          {formError ? (
            <Callout title="Check the balances" tone="warning">
              {formError}
            </Callout>
          ) : null}
          {saveMutation.error ? (
            <Callout title="Account not saved" tone="urgent">
              {saveMutation.error.message}
            </Callout>
          ) : null}
        </form>
      )}
    </Modal>
  );
}

function editableMinor(minor: string): string {
  try {
    const value = BigInt(minor);
    const negative = value < 0n;
    const absolute = negative ? -value : value;
    return (
      (negative ? "-" : "") +
      String(absolute / 100n) +
      "." +
      String(absolute % 100n).padStart(2, "0")
    );
  } catch {
    return "";
  }
}

export function ConnectionsSettingsPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [activeAccount, setActiveAccount] = useState<AccountRead | "new" | null>(null);
  const query = useQuery({ queryKey: ["accounts"], queryFn: () => api.getAccounts() });
  const connectionsQuery = useQuery({
    queryKey: ["connections"],
    queryFn: () => api.getConnections(),
  });
  const connectMutation = useMutation({
    mutationFn: async () => {
      const session = (await api.createPlaidLinkSession()).data;
      sessionStorage.setItem(PLAID_OAUTH_TOKEN_KEY, session.linkToken);
      sessionStorage.setItem(PLAID_OAUTH_RETURN_PATH_KEY, "/settings/connections");
      const publicToken = await openPlaidLink(session.linkToken);
      if (!publicToken) return;
      await api.exchangePlaidPublicToken(publicToken);
      sessionStorage.removeItem(PLAID_OAUTH_TOKEN_KEY);
      sessionStorage.removeItem(PLAID_OAUTH_RETURN_PATH_KEY);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["connections"] });
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
  const updateMutation = useMutation({
    mutationFn: async (connectionId: string) => {
      const session = (await api.createPlaidUpdateSession(connectionId)).data;
      sessionStorage.setItem(PLAID_OAUTH_TOKEN_KEY, session.linkToken);
      sessionStorage.setItem(PLAID_OAUTH_RETURN_PATH_KEY, "/settings/connections");
      await openPlaidLink(session.linkToken);
      sessionStorage.removeItem(PLAID_OAUTH_TOKEN_KEY);
      sessionStorage.removeItem(PLAID_OAUTH_RETURN_PATH_KEY);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["connections"] });
    },
  });
  const disconnectMutation = useMutation({
    mutationFn: (connectionId: string) => api.disconnectPlaid(connectionId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["connections"] });
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
  const accounts = query.data?.data ?? [];
  const plaidConnections =
    connectionsQuery.data?.data.filter((connection) => connection.provider === "plaid") ?? [];
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Manual, CSV, and connected providers"
        title="Accounts & imports"
        description="See each data source, balance freshness, purpose, and protection."
      />
      <SettingsNavigation />
      <Callout title="Plaid Sandbox uses synthetic data only" tone="info">
        Sandbox institutions do not access a real bank. Use Plaid&apos;s test credentials to
        evaluate connection, synchronization, correction, and reconnect flows safely.
      </Callout>
      <div className="button-row">
        <Button
          type="button"
          isLoading={connectMutation.isPending}
          onClick={() => connectMutation.mutate()}
        >
          Connect a Sandbox bank
        </Button>
        <Button type="button" onClick={() => setActiveAccount("new")}>
          Add manual account
        </Button>
        <Link className="mm-button mm-button--secondary" to="/activity/import">
          Import CSV
        </Link>
        <Link className="text-link" to="/activity/import/history">
          View import history
        </Link>
      </div>
      {connectMutation.error ? (
        <Callout title="Bank not connected" tone="urgent">
          {connectMutation.error.message}
        </Callout>
      ) : null}
      <QueryState
        isPending={connectionsQuery.isPending}
        isError={connectionsQuery.isError}
        error={connectionsQuery.error}
        onRetry={() => void connectionsQuery.refetch()}
      >
        {plaidConnections.length > 0 ? (
          <div className="card-list">
            {plaidConnections.map((connection) => (
              <Card key={connection.id}>
                <div className="list-row">
                  <div className="list-row__main">
                    <strong>Connected bank</strong>
                    <span>
                      {titleFromMachineValue(connection.providerEnvironment ?? "sandbox")} ·{" "}
                      {titleFromMachineValue(connection.status)}
                      {connection.lastSuccessAt
                        ? ` · synced ${formatInstant(connection.lastSuccessAt, "UTC")}`
                        : " · first sync pending"}
                    </span>
                  </div>
                  <StatusBadge
                    status={
                      connection.status === "active"
                        ? "covered"
                        : connection.status === "syncing"
                          ? "updating"
                          : connection.status === "stale"
                            ? "data_stale"
                            : connection.status === "reauth_required" ||
                                connection.status === "error"
                              ? "at_risk"
                              : "pending"
                    }
                  />
                </div>
                <div className="button-row">
                  {connection.status === "reauth_required" || connection.status === "error" ? (
                    <Button
                      type="button"
                      variant="secondary"
                      isLoading={updateMutation.isPending}
                      onClick={() => updateMutation.mutate(connection.id)}
                    >
                      Reconnect
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="quiet"
                    isLoading={disconnectMutation.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          "Disconnect this Sandbox bank? Imported history remains auditable, but automatic updates stop.",
                        )
                      ) {
                        disconnectMutation.mutate(connection.id);
                      }
                    }}
                  >
                    Disconnect
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        ) : null}
      </QueryState>
      {updateMutation.error || disconnectMutation.error ? (
        <Callout title="Connection not changed" tone="urgent">
          {(updateMutation.error ?? disconnectMutation.error)?.message}
        </Callout>
      ) : null}
      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
      >
        {accounts.length === 0 ? (
          <StatePanel title="No accounts yet">
            Add an account manually, then import its activity from a bank or card CSV.
          </StatePanel>
        ) : (
          <div className="card-list">
            {accounts.map((account) => (
              <Card key={account.id}>
                <div className="list-row">
                  <div className="list-row__main">
                    <strong>{account.name}</strong>
                    <span>
                      {account.source === "provider"
                        ? "Plaid-connected"
                        : account.source === "csv"
                          ? "CSV-maintained"
                          : "Manual"}{" "}
                      · {account.purposeName ?? "Purpose not assigned"} · balance as of{" "}
                      {formatInstant(account.balanceAsOf, "UTC")}
                    </span>
                  </div>
                  <div className="list-row__side">
                    <strong>{formatMoney(account.currentBalanceMinor, account.currency)}</strong>
                    <Button type="button" variant="quiet" onClick={() => setActiveAccount(account)}>
                      Edit
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </QueryState>
      <AccountEditor
        account={activeAccount && activeAccount !== "new" ? activeAccount : null}
        isOpen={activeAccount !== null}
        onClose={() => setActiveAccount(null)}
      />
    </div>
  );
}

type RulesForm = {
  financialHorizonDays: string;
  householdBuffer: string;
  minimumAction: string;
  debtStrategy: SettingsRead["household"]["debtStrategy"];
};

export function RulesSettingsPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({ queryKey: ["settings"], queryFn: () => api.getSettings() });
  const purposesQuery = useQuery({ queryKey: ["purposes"], queryFn: () => api.getPurposes() });
  const settings = settingsQuery.data?.data;
  const { register, handleSubmit } = useForm<RulesForm>({
    values: {
      financialHorizonDays: String(settings?.household.financialHorizonDays ?? 14),
      householdBuffer: settings ? editableMinor(settings.household.householdBufferMinor) : "",
      minimumAction: settings ? editableMinor(settings.household.minimumActionMinor) : "",
      debtStrategy: settings?.household.debtStrategy ?? "avalanche",
    },
  });
  const mutation = useMutation({
    mutationFn: (input: Partial<SettingsRead>) => {
      if (!settings) throw new Error("Settings are not loaded.");
      return api.updateSettings(input, settings.version);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings"] });
      await queryClient.invalidateQueries({ queryKey: ["briefing"] });
    },
  });
  const submit = handleSubmit((values) => {
    const householdBufferMinor = parseMoneyInput(values.householdBuffer || "0");
    const minimumActionMinor = parseMoneyInput(values.minimumAction || "0");
    if (!householdBufferMinor || !minimumActionMinor) return;
    mutation.mutate({
      household: {
        ...(settings?.household ?? {
          name: "",
          baseCurrency: "USD",
          financialHorizonDays: 14,
          householdBufferMinor: "0",
          minimumActionMinor: "0",
          debtStrategy: "avalanche",
        }),
        financialHorizonDays: Number(values.financialHorizonDays),
        householdBufferMinor,
        minimumActionMinor,
        debtStrategy: values.debtStrategy,
      },
    });
  });
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Versioned household rules"
        title="Rules"
        description="Changing a financial rule produces a new rule-set version and calculation; prior snapshots remain traceable."
      />
      <SettingsNavigation />
      <QueryState
        isPending={settingsQuery.isPending}
        isError={settingsQuery.isError}
        error={settingsQuery.error}
        onRetry={() => void settingsQuery.refetch()}
      >
        {settings ? (
          <>
            <Card>
              <SectionHeader title="Coverage, buffers, and priorities" />
              <form className="form-stack" onSubmit={submit}>
                <div className="form-grid form-grid--two">
                  <FormField label="Default horizon days" htmlFor="settings-horizon">
                    <TextInput
                      id="settings-horizon"
                      type="number"
                      min={1}
                      max={366}
                      {...register("financialHorizonDays")}
                    />
                  </FormField>
                  <FormField
                    label={"Household buffer (" + settings.household.baseCurrency + ")"}
                    htmlFor="settings-buffer"
                  >
                    <TextInput
                      id="settings-buffer"
                      inputMode="decimal"
                      {...register("householdBuffer")}
                    />
                  </FormField>
                  <FormField
                    label="Minimum recommendation amount"
                    htmlFor="settings-minimum-action"
                  >
                    <TextInput
                      id="settings-minimum-action"
                      inputMode="decimal"
                      {...register("minimumAction")}
                    />
                  </FormField>
                  <FormField label="Debt strategy" htmlFor="settings-debt-strategy">
                    <SelectInput id="settings-debt-strategy" {...register("debtStrategy")}>
                      <option value="avalanche">Avalanche</option>
                      <option value="snowball">Snowball</option>
                      <option value="custom">Custom order</option>
                    </SelectInput>
                  </FormField>
                </div>
                {mutation.error ? (
                  <Callout title="Rules not saved" tone="urgent">
                    {mutation.error.message}
                  </Callout>
                ) : null}
                {mutation.isSuccess ? (
                  <Callout title="Plan updated" tone="positive" role="status">
                    A new calculation was requested with the updated rules.
                  </Callout>
                ) : null}
                <Button type="submit" isLoading={mutation.isPending}>
                  Save rules
                </Button>
              </form>
            </Card>
            <Card>
              <SectionHeader
                title="Purpose rules"
                description="Protection and priority belong to purposes, not merchant categories alone."
              />
              <div className="card-list">
                {(purposesQuery.data?.data ?? []).map((purpose) => (
                  <div className="list-row" key={purpose.id}>
                    <div className="list-row__main">
                      <strong>{purpose.name}</strong>
                      <span>
                        {titleFromMachineValue(purpose.type)} · priority {purpose.priority} ·{" "}
                        {titleFromMachineValue(purpose.protectionLevel)}
                      </span>
                    </div>
                    <StatusBadge
                      status={purpose.protectionLevel === "none" ? "pending" : "covered"}
                    />
                  </div>
                ))}
              </div>
            </Card>
            <Card>
              <SectionHeader title="Income, bill, and Spark rules" />
              <p className="muted">
                Income cadence, bill recurrence, flexible due behavior, Spark levels, workable days,
                and tip treatment remain editable on their source records and are applied by the
                versioned engine.
              </p>
              <div className="button-row">
                <Link className="mm-button mm-button--secondary" to="/plan">
                  Edit obligations
                </Link>
                <Link className="mm-button mm-button--secondary" to="/spark">
                  Edit Spark inputs
                </Link>
              </div>
            </Card>
          </>
        ) : null}
      </QueryState>
    </div>
  );
}

export function NotificationsSettingsPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["settings"], queryFn: () => api.getSettings() });
  const settings = query.data?.data;
  const [email, setEmail] = useState<boolean | null>(null);
  const [push, setPush] = useState<boolean | null>(null);
  const mutation = useMutation({
    mutationFn: () => {
      if (!settings) throw new Error("Settings are not loaded.");
      return api.updateSettings(
        {
          notifications: {
            inApp: true,
            email: email ?? settings.notifications.email,
            push: push ?? settings.notifications.push,
          },
        },
        settings.version,
      );
    },
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["settings"] }),
  });
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Quiet by default"
        title="Notifications"
        description="Critical issues may notify promptly; routine changes belong in the daily briefing."
      />
      <SettingsNavigation />
      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
      >
        {settings ? (
          <Card>
            <div className="form-stack">
              <label className="check-row">
                <input type="checkbox" checked disabled />
                <span>
                  <strong>In-app</strong>
                  <br />
                  <span className="small muted">
                    Required source of truth for alerts and plan changes.
                  </span>
                </span>
              </label>
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={email ?? settings.notifications.email}
                  onChange={(event) => setEmail(event.currentTarget.checked)}
                />
                <span>
                  <strong>Email</strong>
                  <br />
                  <span className="small muted">
                    Generic preview only; no balances, merchants, account masks, or notes.
                  </span>
                </span>
              </label>
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={push ?? settings.notifications.push}
                  onChange={(event) => setPush(event.currentTarget.checked)}
                />
                <span>
                  <strong>Web push</strong>
                  <br />
                  <span className="small muted">
                    Requires explicit browser permission after saving.
                  </span>
                </span>
              </label>
              {mutation.error ? (
                <Callout title="Preferences not saved" tone="urgent">
                  {mutation.error.message}
                </Callout>
              ) : null}
              <Button
                type="button"
                isLoading={mutation.isPending}
                onClick={() => mutation.mutate()}
              >
                Save notification preferences
              </Button>
            </div>
          </Card>
        ) : null}
      </QueryState>
    </div>
  );
}

export function PrivacySettingsPage() {
  const api = useApi();
  const { reauthenticate } = useAuth();
  const navigate = useNavigate();
  const [exportId, setExportId] = useState<string>();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const settingsQuery = useQuery({ queryKey: ["settings"], queryFn: () => api.getSettings() });
  const exportMutation = useMutation({
    mutationFn: () => api.requestExport(),
    onSuccess: (response) => setExportId(response.data.id),
  });
  const exportQuery = useQuery({
    queryKey: ["data-export", exportId],
    queryFn: () => api.getExport(exportId ?? ""),
    enabled: Boolean(exportId),
    refetchInterval: (query) => (query.state.data?.data.status === "ready" ? false : 2_000),
  });
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const recentAccessToken = await reauthenticate(password);
      return new MoneyMapApiClient(recentAccessToken).requestAccountDeletion({ confirmation });
    },
    onSuccess: () => navigate("/login", { replace: true }),
  });
  const settings = settingsQuery.data?.data;
  const exportState = exportQuery.data?.data;
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Data control"
        title="Privacy & data"
        description="Inspect what IterNest Money keeps, export it in a usable format, or start permanent deletion after recent authentication."
      />
      <SettingsNavigation />
      <QueryState
        isPending={settingsQuery.isPending}
        isError={settingsQuery.isError}
        error={settingsQuery.error}
        onRetry={() => void settingsQuery.refetch()}
      >
        {settings ? (
          <>
            <Card>
              <SectionHeader title="Privacy model" />
              <p>{settings.privacy.dataRetentionSummary}</p>
              <ul>
                <li>
                  Authentication uses Supabase Auth; no service-role credential is shipped to this
                  browser.
                </li>
                <li>
                  Manual and CSV financial records are household-scoped and server-authorized.
                </li>
                <li>
                  Analytics exclude amounts, balances, merchants, account masks, due dates,
                  transaction IDs, and free text.
                </li>
                <li>IterNest Money does not store bank login credentials or initiate payments.</li>
              </ul>
            </Card>
            <Card>
              <SectionHeader
                title="Export household data"
                description="A durable workflow creates an encrypted archive and short-lived download."
              />
              {!exportId ? (
                <Button
                  type="button"
                  isLoading={exportMutation.isPending}
                  onClick={() => exportMutation.mutate()}
                >
                  Request export
                </Button>
              ) : exportState?.status === "ready" && exportState.downloadUrl ? (
                <a className="mm-button mm-button--primary" href={exportState.downloadUrl}>
                  Download export
                </a>
              ) : (
                <Callout title="Export in progress" tone="info" role="status">
                  Status: {titleFromMachineValue(exportState?.status ?? "requested")}
                </Callout>
              )}
              {exportMutation.error ? (
                <Callout title="Export not started" tone="urgent">
                  {exportMutation.error.message}
                </Callout>
              ) : null}
              {exportState?.expiresAt ? (
                <p className="small muted">
                  Download expires {formatInstant(exportState.expiresAt, settings.profile.timezone)}
                  .
                </p>
              ) : null}
            </Card>
            <Card className="danger-zone">
              <SectionHeader
                title="Permanently delete your IterNest Money account"
                description="Deletion disconnects providers, stops jobs and notifications, deletes active financial data, then deletes the auth identity."
              />
              <Button type="button" variant="danger" onClick={() => setIsDeleteOpen(true)}>
                Start account deletion
              </Button>
            </Card>
          </>
        ) : null}
      </QueryState>
      <Modal
        isOpen={isDeleteOpen}
        title="Permanently delete this household?"
        description="This operation is durable and cannot be undone after completion."
        onClose={() => setIsDeleteOpen(false)}
        footer={
          <>
            <Button type="button" variant="quiet" onClick={() => setIsDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={!password || confirmation !== "DELETE MY MONEYMAP"}
              isLoading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              Permanently delete
            </Button>
          </>
        }
      >
        <div className="form-stack">
          <Callout title="Recent authentication required" tone="urgent">
            The server verifies your password before starting the deletion workflow. A recoverable
            failure remains visible rather than leaving a half-deleted account.
          </Callout>
          <FormField label="Current password" htmlFor="delete-account-password">
            <TextInput
              id="delete-account-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.currentTarget.value)}
            />
          </FormField>
          <FormField label="Type DELETE MY MONEYMAP" htmlFor="delete-account-phrase">
            <TextInput
              id="delete-account-phrase"
              value={confirmation}
              onChange={(event) => setConfirmation(event.currentTarget.value)}
            />
          </FormField>
          {deleteMutation.error ? (
            <Callout title="Deletion not started" tone="urgent">
              {deleteMutation.error.message}
            </Callout>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}

export function FeatureFlagsSettingsPage() {
  const api = useApi();
  const query = useQuery({ queryKey: ["settings"], queryFn: () => api.getSettings() });
  const flags = query.data?.data.featureFlags ?? [];
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Release visibility"
        title="Feature flags"
        description="Flags expose release state only. They cannot silently change financial math without a rule-set version change."
      />
      <SettingsNavigation />
      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
      >
        {flags.length === 0 ? (
          <StatePanel title="No household feature flags">
            All enabled Version 1 capabilities use their normal production paths.
          </StatePanel>
        ) : (
          <Card>
            <div className="card-list">
              {flags.map((flag) => (
                <div className="list-row" key={flag.key}>
                  <div className="list-row__main">
                    <strong>{flag.label}</strong>
                    <span>{flag.description}</span>
                  </div>
                  <StatusBadge status={flag.enabled ? "covered" : "pending"} />
                </div>
              ))}
            </div>
          </Card>
        )}
      </QueryState>
    </div>
  );
}
